/**
 * Resolve a live pathname to the in-studio screen it represents (ENG-4324).
 *
 * Pure matcher over the IN_STUDIO_SCREEN_EVENTS SSOT: the screen tracker mounts
 * at the app root (above the router's matched route), so `useParams()` is empty
 * there — the `:roomId` must be lifted from the pathname via `matchPath`, exactly
 * as pageview/route-mapper.ts does for the generic $pageview.
 *
 * Per rule: never-use-type-assertions (ai.knowledge/global) — no `as`; the union
 * type flows from the SSOT through Object.entries.
 * Per rule: reference-rules-in-comments (ai.knowledge/global).
 */

import { matchPath } from 'react-router-dom';
import { IN_STUDIO_SCREEN_EVENTS, type ScreenEventName } from './screen-events.js';

export interface ResolvedScreen {
  /** The canonical `screen_*` name to emit. */
  name: ScreenEventName;
  /** The room the screen belongs to — the "which room" identity Product wants. */
  roomId: string;
}

/**
 * @param pathname a react-router location pathname (no query string)
 * @returns the resolved screen + room id, or null when `pathname` is not an
 *          in-studio screen (login, signup, agent-setup, unmapped routes).
 */
export function resolveScreenForPath(pathname: string): ResolvedScreen | null {
  for (const [pattern, name] of Object.entries(IN_STUDIO_SCREEN_EVENTS)) {
    const match = matchPath(pattern, pathname);
    if (!match) {
      continue;
    }

    // Narrowing, not a runtime case: every pattern in the SSOT declares :roomId, so
    // a match always carries one, but `params` is typed as optional. This is what
    // gives `roomId: string` without an `as` (rule: never-use-type-assertions,
    // ai.knowledge/global). matchPath refuses to match a param against an absent or
    // empty segment, so `/room/` and `/room//boot` never reach here — pinned by the
    // roomless cases in resolve-screen.test.ts, which is what would catch a
    // react-router upgrade that changed it.
    const roomId = match.params.roomId;
    if (roomId === undefined) {
      return null;
    }

    return { name, roomId };
  }

  return null;
}
